import './quick-add.scss'
import ModalDialog from '../global/custom-elements/modal-dialog';

export default class QuickAddModal extends ModalDialog {
    /**
     * Class constructor
     *
     * @arg {object} - `el` Selector for the media gallery
    */
    constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddModalContent"]');
    }

    hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        setTimeout(() => {
            this.modalContent.innerHTML = '';
        }, 300);

        if (preventFocus) this.openedBy = null;
        super.hide();
    }

    show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
            .then((response) => response.text())
            .then((responseText) => {
                const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
                this.productElement = responseHTML.querySelector('[id^="MainProduct-"]');
                this.preventDuplicatedIDs();
                this.removeDOMElements();
                this.adjustDOMElements();
                this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

                if (window.Shopify && window.Shopify.PaymentButton) {
                    window.Shopify.PaymentButton.init();
                }

                if (window.ProductModel) window.ProductModel.loadShopifyXR();

                this.removeGalleryListSemantic();
                this.preventVariantURLSwitching();
                super.show(opener);
            })
            .finally(() => {
                opener.removeAttribute('aria-disabled');
                opener.classList.remove('loading');
                opener.querySelector('.loading-overlay__spinner').classList.add('hidden');
            });
    }

    setInnerHTML(element, html) {
        element.innerHTML = html;

        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        element.querySelectorAll('script').forEach(oldScriptTag => {
            const newScriptTag = document.createElement('script');
            Array.from(oldScriptTag.attributes).forEach(attribute => {
                newScriptTag.setAttribute(attribute.name, attribute.value)
            });
            newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
            oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
    }

    preventVariantURLSwitching() {
        const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
        if (!variantPicker) return;
        variantPicker.setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
        const deleteDomElements = [
            'nav',
            'pickup-availability',
            'product-modal',
            '.swiper',
            '.swiper-scrollbar',
            '.js-quick-add-context-breadcrumb',
            '.js-product-accordion',
            '.js-product-media',
            '.js-quick-add-context-wysiwyg',
            '.js-quick-add-context-free-delivery-banner',
            '.js-quick-add-context-wtb-ctas',
            '.js-product-kf-images',
            '.js-product-kf-icons',
            '.js-pdp-cta-sticky',
            '.js-quick-add-context-reviews'
        ];

        deleteDomElements.forEach(selector => {
            const elements = this.productElement.querySelectorAll(selector);
            if (elements.length > 0) {
                elements.forEach(element => element.remove());
            }
        });

        this.keepOnlyQuickAddEssentials();
    }

    keepOnlyQuickAddEssentials() {
        const productInfo = this.productElement.querySelector('[id^="ProductInfo-"]');
        if (!productInfo) return;

        const keepSelectors = [
            '.js-quick-add-context-product-type',
            '.js-quick-add-context-heading',
            '.js-quick-add-context-price-container',
            '.js-quick-add-context-variants',
            '.js-quick-add-context-quantity-atc'
        ];

        Array.from(productInfo.children).forEach(child => {
            const shouldKeep = keepSelectors.some(sel => child.matches(sel));
            if (!shouldKeep) child.remove();
        });
    }

    adjustDOMElements() {
        const wrap = this.productElement.querySelector('.js-quick-add-context-wrap');
        if (wrap) {
            wrap.classList.add('mediumMax:wrap', '[&>div]:h-full', 'h-full', 'medium:px-10');
            wrap.classList.remove('medium:wrap', 'pb-[1.875rem]', 'xLarge:pb-12');
        }

        const container = this.productElement.querySelector('.js-quick-add-context-container');
        if (container) {
            container.classList.add('pt-3', 'pb-6', 'large:pt-10', 'large:pb-6');
            container.classList.remove('container');
        }

        const productGrid = this.productElement.querySelector('.js-quick-add-context-grid');
        if (productGrid) {
            productGrid.classList.remove('grid', 'grid-cols-4', 'medium:grid-cols-12', 'medium:gap-x-5');
            productGrid.classList.add('h-full')
        }

        const modalMoreInfo = this.productElement.querySelector('.js-quick-add-more-info-cta, .product__modal-more-info');
        if (modalMoreInfo) {
            modalMoreInfo.classList.remove('hidden');
            modalMoreInfo.classList.add('inline-block');
        }

        const productCarousel = this.productElement.querySelector('.js-pdp-media-gallery');
        if (productCarousel) productCarousel.classList.remove('js-pdp-media-gallery');

        const productInfoContainer = this.productElement.querySelector('.js-quick-add-context-product-info-container');
        if (productInfoContainer) {
            productInfoContainer.classList.remove('col-span-4', 'mediumMax:mt-9', 'mediumMax:wrap', 'medium:col-span-6', 'xLarge:medium:col-start-8', 'xLarge:medium-col-span-5');
            productInfoContainer.classList.add('mediumMax:mt-[.875rem]', '[&>div]:h-full', 'h-full');
        }

        const productInfo = this.productElement.querySelector('.js-quick-add-context-product-info');
        if (productInfo) {
            productInfo.classList.add('relative', 'flex', 'flex-col');
        }

        const h1 = this.productElement.querySelector('.js-quick-add-context-heading');
        if (h1) {
            h1.classList.remove('text-[1.75rem]', 'font-bold', 'large:heading-r')
            h1.classList.add('heading-xs', 'me-20', 'font-medium', 'medium:me-24');
        }

        const priceSwap = this.productElement.querySelector('.js-quick-add-context-price');
        if (priceSwap) {
            priceSwap.classList.remove('mt-5');
        }

        const priceSwapContainer = this.productElement.querySelector('.js-quick-add-context-price-container');
        if (priceSwapContainer) {
            priceSwapContainer.classList.remove('mt-6', 'large:mt-8');
            priceSwapContainer.classList.add('mt-2.5', 'large:mt-[1.125rem]');
        }

        const variants = this.productElement.querySelector('.js-quick-add-context-variants');
        if (variants) {
            variants.classList.remove('mt-5', 'xLarge:mt-8');
            variants.classList.add('mt-[1.875rem]', 'large:mt-9');
        }

        const variantLabels = this.productElement.querySelectorAll('.js-quick-add-context-variants-label');
        if (variantLabels.length) {
            variantLabels.forEach(label => {
                label.classList.add('variant-label-plp');
            });
        }

        const quantityAtc = this.productElement.querySelector('.js-quick-add-context-quantity-atc');
        if (quantityAtc) {
            quantityAtc.classList.remove('items-center', 'gap-2', 'mt-9', 'xLarge:gap-5', 'xLarge:mt-10');
            quantityAtc.classList.add('h-full', 'flex-wrap', 'flex-col', 'justify-between', 'gap-4', 'medium:gap-5', 'medium:mt-3');
        }

        const quantityInput = this.productElement.querySelector('.js-quick-add-context-quantity-input');
        if (quantityInput) {
            quantityInput.classList.add('w-full');
            quantityInput
                .querySelectorAll('.quantity')
                .forEach(btn => btn.classList.add('quantity--variant-modal'));
            quantityInput
                .querySelectorAll('.quantity__button')
                .forEach(btn => btn.classList.add('quantity__button--variant-modal'));
            quantityInput
                .querySelectorAll('.quantity__input')
                .forEach(btn => btn.classList.add('quantity__input--variant-modal'));
        }

        const productForm = this.productElement.querySelector('.js-quick-add-context-product-form');
        if (productForm) {
            productForm.classList.remove('w-full');
            productForm.classList.add('inline-block', 'w-auto', 'flex-1');
        }

        const productFormContainer = this.productElement.querySelector('.js-quick-add-context-product-form-container');
        if (productFormContainer) {
            productFormContainer.classList.add('flex', 'gap-x-2');
        }

        const submit = this.productElement.querySelector('.js-quick-add-context-submit');
        if (submit) {
            submit.classList.remove('button--atc-pdp');
            submit.classList.add('button--secondary')
            submit.insertAdjacentHTML(
                'beforeend',
                `
                <span class="button__icon">
                    <span class="button__icon-chevron">
                        <svg viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                            d="M2 1L8 8L2 15"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            />
                        </svg>
                    </span>
                    <span class="button__icon-chevron">
                        <svg viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                            d="M2 1L8 8L2 15"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            />
                        </svg>
                    </span>
                </span>
                `
            );
        }
    }

    preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
        this.productElement.querySelectorAll('variant-selects, variant-radios').forEach((variantSelect) => {
            variantSelect.dataset.originalSection = sectionId;
        });
    }

    removeGalleryListSemantic() {
        const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }
}
